import React, { useEffect, useState } from 'react';
import { Bell, BellOff, X, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';

const SHOWN_KEY = 'ek-push-prompt-v1';
const SNOOZE_DAYS = 21;

const isStandalone = () => window.matchMedia('(display-mode: standalone)').matches
  || (window.navigator as any).standalone === true;

const urlB64ToUint8 = (b64: string): Uint8Array => {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

const snoozed = (): boolean => {
  try {
    const raw = localStorage.getItem(SHOWN_KEY);
    if (!raw) return false;
    const { at, state } = JSON.parse(raw);
    if (state === 'subscribed' || state === 'blocked') return true;
    const days = (Date.now() - new Date(at).getTime()) / (1000 * 60 * 60 * 24);
    return days < SNOOZE_DAYS;
  } catch {
    return false;
  }
};

const mark = (state: 'dismissed' | 'subscribed' | 'blocked') => {
  try { localStorage.setItem(SHOWN_KEY, JSON.stringify({ state, at: new Date().toISOString() })); } catch { /* ignore */ }
};

export default function PushOptIn() {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [publicKey, setPublicKey] = useState<string | null>(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    if (!isStandalone()) return; // only ask once the PWA is installed
    if (snoozed()) return;
    if (Notification.permission === 'denied') { mark('blocked'); return; }

    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const existing = await reg.pushManager.getSubscription();
        if (existing) { mark('subscribed'); return; }
      } catch { /* ignore */ }
      try {
        const { data } = await api.get<{ public_key: string; configured: boolean }>('/public/push/key');
        if (!data.configured || !data.public_key) return;
        setPublicKey(data.public_key);
        window.setTimeout(() => setVisible(true), 3000);
      } catch { /* ignore */ }
    })();
  }, []);

  const dismiss = () => {
    mark('dismissed');
    setVisible(false);
  };

  const subscribe = async () => {
    if (!publicKey) return;
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        if (perm === 'denied') mark('blocked');
        setVisible(false);
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8(publicKey),
      });
      const raw = sub.toJSON();
      await api.post('/public/push/subscribe', {
        endpoint: raw.endpoint,
        keys: raw.keys || {},
        expiration_time: raw.expirationTime || null,
      });
      mark('subscribed');
      setVisible(false);
    } catch (e) {
      /* soft-fail — leave snooze stamp so we don't spam */
      mark('dismissed');
      setVisible(false);
    } finally {
      setBusy(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="ek-push" role="dialog" aria-labelledby="ek-push-title" data-testid="push-opt-in">
      <span className="ek-push-icon" aria-hidden="true"><Bell size={16} /></span>
      <div className="ek-push-text">
        <strong id="ek-push-title">Get notified of new releases</strong>
        <span>One ping the day a track drops. Nothing else — unsubscribe anytime from your phone settings.</span>
      </div>
      <div className="ek-push-actions">
        <button type="button" className="ek-push-btn" onClick={subscribe} disabled={busy} data-testid="push-opt-in-accept">
          {busy ? <Loader2 size={14} className="ek-spin" /> : <Bell size={14} />} Notify me
        </button>
        <button type="button" className="ek-push-close" onClick={dismiss} aria-label="Not now" data-testid="push-opt-in-dismiss">
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
